import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { ShowSummary } from '@fetmode/types';
import { t } from '@fetmode/i18n';
import { Link, useNavigate } from 'react-router-dom';

import { resolveApiUrl } from '@fetmode/app-core';

import { AuthInlineForm, useAuth } from '../auth/index.js';
import { useShowRealtime } from '../shows/useShowRealtime.js';
import { camBadgeClass, camBadgeLabel } from './cam-show-badge.js';
import { featuredThumbUrl } from '../../shared/placeholder-media.js';
import '../../shared/styles/thumb-grid.css';
import './LandingPage.css';

const apiBaseUrl = resolveApiUrl(import.meta.env);

export function LandingPage(): ReactElement {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [featured, setFeatured] = useState<ShowSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(() => {
    void api
      .listCams()
      .then((result) => {
        setFeatured(result.online.slice(0, 6));
      })
      .catch(() => {
        setFeatured([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [api]);

  useEffect(() => {
    setIsLoading(true);
    refresh();
  }, [refresh]);

  useShowRealtime(
    apiBaseUrl,
    { cams: true },
    {
      onCamsChanged: () => {
        refresh();
      },
    },
  );

  const handleAuthSuccess = (): void => {
    void navigate('/onboarding/experience', { replace: true });
  };

  return (
    <div className="landing">
      <header className="landing-hero">
        <div className="landing-hero__media" aria-hidden />
        <div className="landing-hero__scrim" aria-hidden />
        <div className="landing-hero__inner">
          <div className="landing-hero__copy">
            <p className="landing-hero__brand">{t('common.app.name')}</p>
            <h1 className="landing-hero__title">{t('common.app.tagline')}</h1>
            <p className="landing-hero__body">{t('common.home.landingBody')}</p>
            <Link to="/cams" className="landing-hero__browse">
              {t('common.home.landingBrowse')}
            </Link>
          </div>
          <div className="landing-hero__form">
            <AuthInlineForm initialMode="register" onSuccess={handleAuthSuccess} />
          </div>
        </div>
      </header>

      <section className="landing-featured" aria-labelledby="landing-featured-heading">
        <div className="landing-featured__head">
          <h2 id="landing-featured-heading">{t('common.home.landingFeatured')}</h2>
          <p>{t('common.home.landingFeaturedHint')}</p>
        </div>

        {isLoading ? (
          <p className="landing-featured__muted">{t('common.auth.loading')}</p>
        ) : featured.length === 0 ? (
          <p className="landing-featured__muted">{t('common.home.noLiveShows')}</p>
        ) : (
          <ul className="landing-featured__grid">
            {featured.map((show, index) => {
              const onBreak = show.status === 'PAUSED';
              const isAway = show.status === 'AWAY';
              return (
                <li key={show.id}>
                  <Link
                    to={`/@${show.model.username}`}
                    className="landing-featured__card"
                    aria-label={show.model.displayName}
                  >
                    <img
                      src={featuredThumbUrl(show.model.username, index)}
                      alt=""
                      loading={index < 2 ? 'eager' : 'lazy'}
                      width={900}
                      height={1200}
                    />
                    <span className={`landing-featured__badge ${camBadgeClass(show.type)}`}>
                      {isAway
                        ? t('common.show.away')
                        : onBreak
                          ? t('common.show.onBreak')
                          : camBadgeLabel(show.type)}
                    </span>
                    <span className="landing-featured__meta">
                      <span className="landing-featured__name">{show.model.displayName}</span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}

        <div className="landing-featured__footer">
          <Link to="/cams">{t('common.home.landingSeeAll')}</Link>
          <Link to="/community">{t('common.home.landingCommunity')}</Link>
        </div>
      </section>
    </div>
  );
}
